// src/components/attestations/DocumentUploadModal.jsx
import React, { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import { requestPresignedUpload, confirmDocumentUpload } from '../../store/actions/attestationActions';
import LoadingSpinner from '../common/LoadingSpinner';

const DocumentUploadModal = ({ attestationId, onClose, onUploadComplete }) => {
  const dispatch = useDispatch();
  const [step, setStep] = useState('select'); // 'select', 'metadata', 'uploading'
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [metadata, setMetadata] = useState({
    name: '',
    altText: '',
    caption: '',
    description: '',
    credit: '',
    copyright: '',
    license: 'Tous droits réservés',
    isPublic: true,
    tags: '',
  });
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);

  const licenseOptions = [
    'Tous droits réservés',
    'Creative Commons BY',
    'Creative Commons BY-SA',
    'Creative Commons BY-NC',
    'Creative Commons BY-ND',
    'Domaine public',
    'Licence libre',
  ];

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1100,
    },
    modal: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      width: '90%',
      maxWidth: '600px',
      maxHeight: '90vh',
      overflow: 'hidden',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    },
    header: {
      padding: '1.5rem',
      borderBottom: '1px solid #e5e7eb',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: '#f9fafb',
    },
    title: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: '#111827',
      margin: 0,
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '1.5rem',
      cursor: 'pointer',
      color: '#6b7280',
      padding: '0.5rem',
      borderRadius: '0.375rem',
      transition: 'all 0.2s',
    },
    content: {
      padding: '1.5rem',
      overflowY: 'auto',
      maxHeight: 'calc(90vh - 130px)',
    },
    dropzone: {
      padding: '2rem',
      border: '2px dashed #d1d5db',
      borderRadius: '0.5rem',
      textAlign: 'center',
      cursor: 'pointer',
      backgroundColor: '#f9fafb',
      transition: 'all 0.2s',
    },
    dropzoneActive: {
      borderColor: '#2563eb',
      backgroundColor: '#eff6ff',
    },
    fileIcon: {
      width: '3rem',
      height: '3rem',
      marginBottom: '1rem',
      color: '#9ca3af',
    },
    previewContainer: {
      marginBottom: '1.5rem',
      textAlign: 'center',
    },
    previewImage: {
      maxWidth: '100%',
      maxHeight: '200px',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    },
    fileInfo: {
      padding: '1rem',
      backgroundColor: '#f3f4f6',
      borderRadius: '0.5rem',
      marginBottom: '1.5rem',
    },
    fileName: {
      fontSize: '1rem',
      fontWeight: 500,
      color: '#111827',
      marginBottom: '0.25rem',
    },
    fileDetails: {
      fontSize: '0.875rem',
      color: '#6b7280',
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
    },
    label: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#374151',
      marginBottom: '0.25rem',
    },
    input: {
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      transition: 'border-color 0.2s',
    },
    textarea: {
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      minHeight: '80px',
      resize: 'vertical',
    },
    select: {
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      backgroundColor: '#ffffff',
    },
    checkbox: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      cursor: 'pointer',
    },
    checkboxInput: {
      width: '1rem',
      height: '1rem',
      cursor: 'pointer',
    },
    progressContainer: {
      marginTop: '1rem',
      textAlign: 'center',
    },
    progressBar: {
      height: '0.5rem',
      backgroundColor: '#e5e7eb',
      borderRadius: '9999px',
      overflow: 'hidden',
      marginBottom: '0.5rem',
    },
    progressFill: {
      height: '100%',
      backgroundColor: '#2563eb',
      transition: 'width 0.3s ease',
    },
    progressText: {
      fontSize: '0.875rem',
      color: '#6b7280',
    },
    uploadStatus: {
      marginTop: '1rem',
      padding: '1rem',
      backgroundColor: '#f3f4f6',
      borderRadius: '0.5rem',
      textAlign: 'center',
    },
    footer: {
      padding: '1.5rem',
      borderTop: '1px solid #e5e7eb',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
      backgroundColor: '#f9fafb',
    },
    button: {
      padding: '0.5rem 1rem',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      border: 'none',
      transition: 'all 0.2s',
    },
    cancelButton: {
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
    },
    nextButton: {
      backgroundColor: '#2563eb',
      color: '#ffffff',
    },
    uploadButton: {
      backgroundColor: '#10b981',
      color: '#ffffff',
    },
    error: {
      padding: '0.75rem',
      backgroundColor: '#fee2e2',
      color: '#dc2626',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      marginBottom: '1rem',
    },
  };

  const handleFileSelect = (file) => {
    setSelectedFile(file);
    setMetadata(prev => ({
      ...prev,
      name: file.name,
    }));

    // Generate preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
        
        // Get image dimensions
        const img = new Image();
        img.onload = () => {
          setMetadata(prev => ({
            ...prev,
            width: img.width,
            height: img.height,
          }));
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    }

    setStep('metadata');
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleMetadataChange = (e) => {
    const { name, value, type, checked } = e.target;
    setMetadata(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const uploadFileToS3 = async (uploadUrl, file) => {
    try {
      const response = await axios.put(uploadUrl, file, {
        headers: {
          'Content-Type': file.type,
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentComplete = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentComplete);
          }
        },
      });

      return response.data;
    } catch (error) {
      console.error('S3 upload error:', error);
      throw new Error(error.response?.data?.message || 'Upload failed');
    }
  };

  const handleUpload = async () => {
    setStep('uploading');
    setError(null);
    setUploadProgress(0);

    try {
      // Process tags
      const tagsArray = metadata.tags
        ? metadata.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
        : [];

      // 1. Request presigned URL from backend
      const fileData = {
        originalName: selectedFile.name,
        fileType: selectedFile.type,
        fileSize: selectedFile.size,
        name: metadata.name || selectedFile.name,
        altText: metadata.altText,
        caption: metadata.caption,
        description: metadata.description,
        credit: metadata.credit,
        copyright: metadata.copyright,
        license: metadata.license,
        isPublic: metadata.isPublic,
        tags: tagsArray,
        width: metadata.width,
        height: metadata.height,
      };

      const presignedData = await dispatch(requestPresignedUpload(attestationId, fileData));
      
      // 2. Upload file directly to S3 using presigned URL with Axios
      await uploadFileToS3(presignedData.uploadUrl, selectedFile);
      
      // 3. Confirm upload completion and save document metadata
      await dispatch(confirmDocumentUpload(attestationId, {
        key: presignedData.key,
        fileName: presignedData.fileName,
        originalName: presignedData.originalName,
        fileUrl: presignedData.fileUrl,
        fileSize: selectedFile.size,
        fileType: selectedFile.type,
        metadata: fileData,
      }));

      onUploadComplete();
      onClose();
    } catch (err) {
      setError(err.message || 'Erreur lors du téléchargement');
      setStep('metadata');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>
            {step === 'select' && 'Sélectionner un fichier'}
            {step === 'metadata' && 'Informations du document'}
            {step === 'uploading' && 'Téléchargement en cours'}
          </h3>
          <button
            style={styles.closeButton}
            onClick={onClose}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f3f4f6';
              e.currentTarget.style.color = '#111827';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#6b7280';
            }}
          >
            ×
          </button>
        </div>

        <div style={styles.content}>
          {error && <div style={styles.error}>{error}</div>}

          {step === 'select' && (
            <div
              style={styles.dropzone}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onClick={() => document.getElementById('file-input').click()}
            >
              <svg
                style={styles.fileIcon}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              <div style={{ fontSize: '1rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
                Glissez-déposez votre fichier ici
              </div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
                ou cliquez pour parcourir
              </div>
              <input
                id="file-input"
                type="file"
                onChange={handleFileInput}
                style={{ display: 'none' }}
              />
              <button
                style={styles.button}
                onClick={(e) => {
                  e.stopPropagation();
                  document.getElementById('file-input').click();
                }}
              >
                Parcourir
              </button>
            </div>
          )}

          {step === 'metadata' && selectedFile && (
            <>
              {preview && (
                <div style={styles.previewContainer}>
                  <img src={preview} alt="Preview" style={styles.previewImage} />
                </div>
              )}

              <div style={styles.fileInfo}>
                <div style={styles.fileName}>{selectedFile.name}</div>
                <div style={styles.fileDetails}>
                  {formatFileSize(selectedFile.size)} • {selectedFile.type || 'Type inconnu'}
                </div>
              </div>

              <div style={styles.form}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Nom du document *</label>
                  <input
                    type="text"
                    name="name"
                    value={metadata.name}
                    onChange={handleMetadataChange}
                    style={styles.input}
                    placeholder="Nom du fichier"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Description</label>
                  <textarea
                    name="description"
                    value={metadata.description}
                    onChange={handleMetadataChange}
                    style={styles.textarea}
                    placeholder="Description du document..."
                  />
                </div>

                {selectedFile.type.startsWith('image/') && (
                  <>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Texte alternatif (alt)</label>
                      <input
                        type="text"
                        name="altText"
                        value={metadata.altText}
                        onChange={handleMetadataChange}
                        style={styles.input}
                        placeholder="Description pour les lecteurs d'écran"
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>Légende</label>
                      <input
                        type="text"
                        name="caption"
                        value={metadata.caption}
                        onChange={handleMetadataChange}
                        style={styles.input}
                        placeholder="Légende de l'image"
                      />
                    </div>
                  </>
                )}

                <div style={styles.formGroup}>
                  <label style={styles.label}>Crédit</label>
                  <input
                    type="text"
                    name="credit"
                    value={metadata.credit}
                    onChange={handleMetadataChange}
                    style={styles.input}
                    placeholder="Photographe / Auteur"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Copyright</label>
                  <input
                    type="text"
                    name="copyright"
                    value={metadata.copyright}
                    onChange={handleMetadataChange}
                    style={styles.input}
                    placeholder="© 2024"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Licence</label>
                  <select
                    name="license"
                    value={metadata.license}
                    onChange={handleMetadataChange}
                    style={styles.select}
                  >
                    {licenseOptions.map(license => (
                      <option key={license} value={license}>{license}</option>
                    ))}
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Tags (séparés par des virgules)</label>
                  <input
                    type="text"
                    name="tags"
                    value={metadata.tags}
                    onChange={handleMetadataChange}
                    style={styles.input}
                    placeholder="contrat, assurance, signature, ..."
                  />
                </div>

                <label style={styles.checkbox}>
                  <input
                    type="checkbox"
                    name="isPublic"
                    checked={metadata.isPublic}
                    onChange={handleMetadataChange}
                    style={styles.checkboxInput}
                  />
                  <span style={{ fontSize: '0.875rem', color: '#374151' }}>
                    Document public (visible par tous)
                  </span>
                </label>
              </div>
            </>
          )}

          {step === 'uploading' && (
            <div style={styles.progressContainer}>
              <div style={styles.progressBar}>
                <div style={{ ...styles.progressFill, width: `${uploadProgress}%` }} />
              </div>
              <div style={styles.progressText}>
                {uploadProgress < 100 
                  ? `Téléchargement... ${uploadProgress}%` 
                  : 'Finalisation...'}
              </div>
              {uploadProgress === 100 && (
                <div style={styles.uploadStatus}>
                  <LoadingSpinner size="small" />
                  <div style={{ marginTop: '0.5rem' }}>Enregistrement des informations...</div>
                </div>
              )}
            </div>
          )}
        </div>

        <div style={styles.footer}>
          <button
            onClick={onClose}
            style={{ ...styles.button, ...styles.cancelButton }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f9fafb';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ffffff';
            }}
          >
            Annuler
          </button>

          {step === 'select' && (
            <button
              onClick={() => document.getElementById('file-input').click()}
              style={{ ...styles.button, ...styles.nextButton }}
            >
              Sélectionner
            </button>
          )}

          {step === 'metadata' && (
            <button
              onClick={handleUpload}
              style={{ ...styles.button, ...styles.uploadButton }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#059669';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#10b981';
              }}
            >
              Télécharger
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadModal;